import { ApolloError, gql } from '@apollo/client';

import type { ErrorMessageTypes } from '../components/alerts/errors/error-map';
import { CONSTANTS } from '../constants';
import { setLocalStorageItem } from '../utils/local-storage/local-storage-operations';

import { getStatusCodeFromApolloError } from './get-status-code-from-apollo-error';
import { SsoApiClient } from './sso-api-client';

/**
 * Check if user has TOTP enrolled
 */
export const checkTotpStatusApi = async (
    sessionToken: string
): Promise<{ totpEnrolled: boolean; error?: ErrorMessageTypes }> => {
    try {
        const { data } = await SsoApiClient.query({
            query: gql`
                query CheckTotpStatus($sessionToken: String!) {
                    totpStatus(sessionToken: $sessionToken)
                        @rest(
                            type: "TotpStatus"
                            path: "/totp/totp-status?sessionToken={args.sessionToken}"
                        ) {
                        totpEnrolled
                    }
                }
            `,
            variables: { sessionToken },
            fetchPolicy: 'network-only',
        });

        return {
            totpEnrolled: data?.totpStatus?.totpEnrolled ?? false,
        };
    } catch (err) {
        const error = err as ApolloError;
        return {
            totpEnrolled: false,
            error: getErrorMessageType(error),
        };
    }
};

/**
 * Request OTP code via SMS (for login verification)
 */
export const requestOtpApi = async (
    sessionToken: string,
    phoneNumber: string
): Promise<{
    success: boolean;
    flowId?: string;
    error?: ErrorMessageTypes;
}> => {
    try {
        const { data } = await SsoApiClient.mutate({
            mutation: gql`
                mutation RequestOtp($input: RequestOtpInput!) {
                    requestOtp(input: $input)
                        @rest(
                            type: "RequestOtpResponse"
                            path: "/totp/request-otp"
                            method: "POST"
                        ) {
                        success
                        flowId
                    }
                }
            `,
            variables: {
                input: {
                    sessionToken,
                    phoneNumber,
                },
            },
        });

        return {
            success: data?.requestOtp?.success ?? false,
            flowId: data?.requestOtp?.flowId,
        };
    } catch (err) {
        const error = err as ApolloError;
        return {
            success: false,
            error: getErrorMessageType(error),
        };
    }
};

/**
 * Verify OTP code
 */
export const verifyOtpApi = async (
    sessionToken: string,
    flowId: string,
    code: string
): Promise<{
    success: boolean;
    sessionToken?: string;
    sessionId?: string;
    deviceToken?: string;
    error?: ErrorMessageTypes;
}> => {
    try {
        const { data } = await SsoApiClient.mutate({
            mutation: gql`
                mutation VerifyOtp($input: VerifyOtpInput!) {
                    verifyOtp(input: $input)
                        @rest(
                            type: "VerifyOtpResponse"
                            path: "/totp/verify-otp"
                            method: "POST"
                        ) {
                        success
                        sessionToken
                        sessionId
                        deviceToken
                    }
                }
            `,
            variables: {
                input: {
                    sessionToken,
                    flowId,
                    code,
                },
            },
        });

        // Store device token if returned
        if (data?.verifyOtp?.deviceToken) {
            setLocalStorageItem(CONSTANTS.tokenKey, data.verifyOtp.deviceToken);
        }

        return {
            success: data?.verifyOtp?.success === true,
            sessionToken: data?.verifyOtp?.sessionToken,
            sessionId: data?.verifyOtp?.sessionId,
            deviceToken: data?.verifyOtp?.deviceToken,
        };
    } catch (err) {
        const error = err as ApolloError;
        return {
            success: false,
            error: getErrorMessageType(error),
        };
    }
};

/**
 * Verify TOTP code
 */
export const verifyTotpApi = async (
    sessionToken: string,
    code: string,
    phoneNumber: string
): Promise<{
    success: boolean;
    sessionToken?: string;
    sessionId?: string;
    deviceToken?: string;
    error?: ErrorMessageTypes;
}> => {
    try {
        const { data } = await SsoApiClient.mutate({
            mutation: gql`
                mutation VerifyTotp($input: VerifyTotpInput!) {
                    verifyTotp(input: $input)
                        @rest(
                            type: "VerifyTotpResponse"
                            path: "/totp/verify-totp"
                            method: "POST"
                        ) {
                        success
                        sessionToken
                        sessionId
                        deviceToken
                    }
                }
            `,
            variables: {
                input: {
                    sessionToken,
                    code,
                    phone: phoneNumber,
                },
            },
        });

        // Check if response is directly on data (not nested under verifyTotp)
        const response = data?.verifyTotp || data;

        // Store device token if returned
        if (response?.deviceToken) {
            setLocalStorageItem(CONSTANTS.tokenKey, response.deviceToken);
        }

        return {
            success: response?.success === true,
            sessionToken: response?.sessionToken,
            sessionId: response?.sessionId,
            deviceToken: response?.deviceToken,
        };
    } catch (err) {
        const error = err as ApolloError;
        // oxlint-disable-next-line no-console
        console.error('[verifyTotpApi] Error:', error);
        return {
            success: false,
            error: getErrorMessageType(error),
        };
    }
};

/**
 * Register TOTP device (generates QR code and secret)
 */
export const registerTotpApi = async (
    sessionToken: string
): Promise<{
    success: boolean;
    qrCodeUrl?: string;
    secret?: string;
    flowId?: string;
    error?: ErrorMessageTypes;
}> => {
    try {
        const { data } = await SsoApiClient.mutate({
            mutation: gql`
                mutation RegisterTotp($input: RegisterTotpInput!) {
                    registerTotp(input: $input)
                        @rest(
                            type: "RegisterTotpResponse"
                            path: "/totp/register-totp"
                            method: "POST"
                        ) {
                        qrCodeUrl
                        secret
                        flowId
                    }
                }
            `,
            variables: {
                input: {
                    sessionToken,
                },
            },
        });

        if (data?.registerTotp) {
            return {
                success: true,
                qrCodeUrl: data.registerTotp.qrCodeUrl,
                secret: data.registerTotp.secret,
                flowId: data.registerTotp.flowId,
            };
        }

        return {
            success: false,
            error: '500_GenericError',
        };
    } catch (err) {
        const error = err as ApolloError;
        return {
            success: false,
            error: getErrorMessageType(error),
        };
    }
};

/**
 * Verify TOTP setup with code
 */
export const verifyTotpSetupApi = async (
    sessionToken: string,
    flowId: string,
    code: string,
    phoneNumber: string
): Promise<{
    success: boolean;
    deviceToken?: string;
    error?: ErrorMessageTypes;
}> => {
    try {
        const { data } = await SsoApiClient.mutate({
            mutation: gql`
                mutation VerifyTotpSetup($input: VerifyTotpSetupInput!) {
                    verifyTotpSetup(input: $input)
                        @rest(
                            type: "VerifyTotpSetupResponse"
                            path: "/totp/verify-totp-setup"
                            method: "POST"
                        ) {
                        success
                        deviceToken
                    }
                }
            `,
            variables: {
                input: {
                    sessionToken,
                    flowId,
                    code,
                    phone: phoneNumber,
                },
            },
        });

        // Store device token if returned
        if (data?.verifyTotpSetup?.deviceToken) {
            setLocalStorageItem(
                CONSTANTS.tokenKey,
                data.verifyTotpSetup.deviceToken
            );
        }

        return {
            success: data?.verifyTotpSetup?.success === true,
            deviceToken: data?.verifyTotpSetup?.deviceToken,
        };
    } catch (err) {
        const error = err as ApolloError;
        return {
            success: false,
            error: getErrorMessageType(error),
        };
    }
};

const getErrorMessageType = (
    error?: ApolloError
): ErrorMessageTypes | undefined => {
    if (error) {
        const statusCode = getStatusCodeFromApolloError(error);
        switch (statusCode) {
            case 400:
                return '400_InvalidCode';
            case 429:
                return '429_MaxAttemptReached';
        }
    }
    return '500_GenericError';
};
